import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, LineChart, Line, ResponsiveContainer,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from "recharts";
import { Plus, Pencil, Trash2, TrendingUp, TrendingDown, Wallet, FileSearch, DollarSign, Receipt } from "lucide-react";

const COLORS = ["#5D3A1F", "#C28A2E", "#6B8E5A", "#D4A547", "#A07A45", "#A6741F", "#2A1810", "#8B5A2B", "#B89260", "#E5B872"];

const fmt$ = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const emptyExpense = {
  date: new Date().toISOString().slice(0, 10),
  category: "Travel",
  amount: 0,
  description: "",
  client_id: "",
  client_name: "",
  workshop_id: "",
  workshop_name: "",
  engagement_type: "workshop",
  notes: "",
};

const ExpenseForm = ({ open, onOpenChange, initial, onSubmit, mode, clients, workshops, ref }) => {
  const [form, setForm] = useState(initial || emptyExpense);
  const [saving, setSaving] = useState(false);

  const handleOpenChange = (v) => { if (v) setForm(initial || emptyExpense); onOpenChange(v); };
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try { await onSubmit({ ...form, amount: Number(form.amount || 0) }); handleOpenChange(false); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="expense-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{mode === "create" ? "Log expense" : "Edit expense"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div className="space-y-2"><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setF("date", e.target.value)} data-testid="exp-date-input" /></div>
          <div className="space-y-2">
            <Label>Category *</Label>
            <Select value={form.category} onValueChange={(v) => setF("category", v)}>
              <SelectTrigger data-testid="exp-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>{ref.expense_categories?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Amount (USD) *</Label><Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setF("amount", e.target.value)} data-testid="exp-amount-input" /></div>
          <div className="space-y-2">
            <Label>Engagement Type</Label>
            <Select value={form.engagement_type} onValueChange={(v) => setF("engagement_type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="workshop">Workshop</SelectItem>
                <SelectItem value="consulting">Consulting</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
                <SelectItem value="admin">Administrative</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Client</Label>
            <Select value={form.client_id || "none"} onValueChange={(v) => {
              if (v === "none") { setF("client_id", ""); setF("client_name", ""); return; }
              const c = clients.find((x) => x.id === v);
              setF("client_id", v); setF("client_name", c?.organization_name || "");
            }}>
              <SelectTrigger data-testid="exp-client-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.organization_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Workshop / Event</Label>
            <Select value={form.workshop_id || "none"} onValueChange={(v) => {
              if (v === "none") { setF("workshop_id", ""); setF("workshop_name", ""); return; }
              const w = workshops.find((x) => x.id === v);
              setF("workshop_id", v); setF("workshop_name", w?.name || "");
            }}>
              <SelectTrigger data-testid="exp-workshop-select"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {workshops.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setF("description", e.target.value)} data-testid="exp-desc-input" placeholder="e.g., Hotel for Bridgetown workshop" /></div>
          <div className="space-y-2 md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setF("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} data-testid="exp-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.amount || Number(form.amount) <= 0} className="bg-[#5D3A1F] hover:bg-[#4A2E18]" data-testid="exp-form-save">
            {saving ? "Saving…" : "Save expense"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KpiCard = ({ icon: Icon, label, value, accent = "primary", subtitle, testid }) => {
  const colors = {
    primary: "#5D3A1F", accent: "#C28A2E", success: "#6B8E5A", warning: "#D4A547", danger: "#A23E3E",
  };
  const c = colors[accent];
  return (
    <div data-testid={testid} className="ccs-card ccs-card-hover p-5 lg:p-6">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${c}14` }}>
        <Icon className="w-5 h-5" strokeWidth={1.8} style={{ color: c }} />
      </div>
      <div className="font-display text-3xl font-semibold mt-4 text-[#2A1810] tracking-tight">{value}</div>
      <div className="text-sm text-[#6B5944] mt-1">{label}</div>
      {subtitle && <div className="text-xs text-[#6B5944]/70 mt-1">{subtitle}</div>}
    </div>
  );
};

const FinancePage = () => {
  const [data, setData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [clients, setClients] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [ref, setRef] = useState({ expense_categories: [] });
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([
      api.get("/finance/dashboard"),
      api.get("/expenses"),
      api.get("/clients"),
      api.get("/workshops"),
      api.get("/reference"),
    ]).then(([d, e, c, w, r]) => {
      setData(d.data); setExpenses(e.data); setClients(c.data); setWorkshops(w.data); setRef(r.data);
    }).finally(() => setLoading(false));
  };
  useEffect(fetchAll, []);

  const onCreate = async (d) => { try { await api.post("/expenses", d); toast.success("Expense logged"); fetchAll(); } catch (e) { toast.error("Failed"); throw e; } };
  const onUpdate = async (d) => { try { await api.put(`/expenses/${editing.id}`, d); toast.success("Updated"); fetchAll(); } catch (e) { toast.error("Failed"); throw e; } };
  const onDelete = async (id) => { if (!window.confirm("Delete this expense?")) return; await api.delete(`/expenses/${id}`); toast.success("Deleted"); fetchAll(); };

  if (loading || !data) return <div className="text-[#6B5944] py-20 text-center font-display">Loading finances…</div>;

  return (
    <div className="space-y-7" data-testid="finance-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Finance</div>
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight mt-1.5">Revenue, Expenses & Profitability</h1>
          <p className="text-[#6B5944] mt-1.5">Practical financial intelligence for your consulting practice.</p>
        </div>
        <Button onClick={() => { setEditing(null); setFormOpen(true); }} data-testid="add-expense-btn"
          className="bg-[#C28A2E] hover:bg-[#A6741F] text-white rounded-xl h-11 px-5 hover:-translate-y-[1px] transition-all">
          <Plus className="w-4 h-4 mr-2" /> Log expense
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5">
        <KpiCard icon={TrendingUp} label="Revenue this month" value={fmt$(data.revenue_this_month)} accent="primary" testid="fin-kpi-rev-month" />
        <KpiCard icon={TrendingDown} label="Expenses this month" value={fmt$(data.expenses_this_month)} accent="accent" testid="fin-kpi-exp-month" />
        <KpiCard icon={Wallet} label="Net Profit (all time)" value={fmt$(data.net_profit)} accent={data.net_profit >= 0 ? "success" : "danger"} testid="fin-kpi-net" />
        <KpiCard icon={FileSearch} label="Outstanding invoices" value={fmt$(data.outstanding_invoices)} accent="warning" testid="fin-kpi-outstanding" />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-[#EBE3D2]/50 rounded-xl">
          <TabsTrigger value="overview" data-testid="fin-tab-overview" className="rounded-lg">Overview</TabsTrigger>
          <TabsTrigger value="profitability" data-testid="fin-tab-profit" className="rounded-lg">Workshop Profitability</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="fin-tab-expenses" className="rounded-lg">Expenses</TabsTrigger>
          <TabsTrigger value="reports" data-testid="fin-tab-reports" className="rounded-lg">Reports</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-5 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="ccs-card p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Revenue vs Expenses</div>
              <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">Last 6 months</h3>
              <div className="mt-4">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.revenue_vs_expense}>
                    <CartesianGrid stroke="#EBE3D2" strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" stroke="#6B5944" tick={{ fontSize: 11 }} />
                    <YAxis stroke="#6B5944" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EBE3D2", borderRadius: 12 }} formatter={(v) => fmt$(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="revenue" fill="#5D3A1F" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="expenses" fill="#C28A2E" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="ccs-card p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Expense Categories</div>
              <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">Where money goes</h3>
              <div className="mt-4">
                {data.expense_categories?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={data.expense_categories} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={3}>
                        {data.expense_categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EBE3D2", borderRadius: 12 }} formatter={(v) => fmt$(v)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-16 text-sm text-[#6B5944]">No expenses logged yet</div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="ccs-card p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Revenue by Client</div>
              <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">Top earners</h3>
              <div className="mt-4 space-y-2.5">
                {data.revenue_by_client?.length > 0 ? data.revenue_by_client.map((row, i) => (
                  <div key={row.client} className="flex items-center justify-between p-3 rounded-lg bg-[#FDFBF5] border border-[#EBE3D2]" data-testid={`rev-client-${i}`}>
                    <div className="font-medium text-sm text-[#2A1810] truncate">{row.client}</div>
                    <div className="font-semibold text-[#5D3A1F]">{fmt$(row.amount)}</div>
                  </div>
                )) : <div className="text-sm text-[#6B5944] text-center py-6">No revenue collected yet</div>}
              </div>
            </div>

            <div className="ccs-card p-6">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Revenue by Service</div>
              <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">Most requested</h3>
              <div className="mt-4 space-y-2.5">
                {data.revenue_by_service?.length > 0 ? data.revenue_by_service.map((row, i) => (
                  <div key={row.service} className="flex items-center justify-between p-3 rounded-lg bg-[#FDFBF5] border border-[#EBE3D2]" data-testid={`rev-service-${i}`}>
                    <div className="font-medium text-sm text-[#2A1810] truncate">{row.service}</div>
                    <div className="font-semibold text-[#5D3A1F]">{fmt$(row.amount)}</div>
                  </div>
                )) : <div className="text-sm text-[#6B5944] text-center py-6">No revenue collected yet</div>}
              </div>
            </div>
          </div>
        </TabsContent>

        {/* PROFITABILITY */}
        <TabsContent value="profitability" className="mt-5">
          <div className="ccs-card overflow-hidden" data-testid="profitability-table">
            <Table>
              <TableHeader>
                <TableRow className="border-[#EBE3D2] hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] py-4">Workshop</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Client</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Revenue</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Expenses</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Profit</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Margin</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.workshop_profitability?.length > 0 ? data.workshop_profitability.map((w) => (
                  <TableRow key={w.workshop_id} className="border-[#EBE3D2]" data-testid={`profit-row-${w.workshop_id}`}>
                    <TableCell className="py-4">
                      <div className="font-medium text-[#2A1810]">{w.name}</div>
                      <div className="text-xs text-[#6B5944]">{w.service_type}</div>
                    </TableCell>
                    <TableCell className="text-sm text-[#6B5944]">{w.client_name || "—"}</TableCell>
                    <TableCell className="text-sm text-[#6B5944]">{w.date}</TableCell>
                    <TableCell className="text-right font-medium text-[#5D3A1F]">{fmt$(w.revenue)}</TableCell>
                    <TableCell className="text-right font-medium text-[#C28A2E]">{fmt$(w.expenses)}</TableCell>
                    <TableCell className={`text-right font-semibold ${w.profit >= 0 ? "text-[#6B8E5A]" : "text-[#A23E3E]"}`}>{fmt$(w.profit)}</TableCell>
                    <TableCell className="text-right">
                      <Badge className={`${w.margin >= 30 ? "bg-[#6B8E5A]/15 text-[#6B8E5A]" : w.margin >= 0 ? "bg-[#D4A547]/15 text-[#A6741F]" : "bg-[#A23E3E]/15 text-[#A23E3E]"} rounded-full font-medium`}>
                        {w.margin}%
                      </Badge>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#6B5944]">No workshop data yet. Add financial values in workshop forms to see profitability.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* EXPENSES */}
        <TabsContent value="expenses" className="mt-5">
          <div className="ccs-card overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#EBE3D2] hover:bg-transparent">
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] py-4">Date</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Category</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Description</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944]">Workshop / Client</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Amount</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.18em] text-[#6B5944] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.length > 0 ? expenses.map((e) => (
                  <TableRow key={e.id} className="border-[#EBE3D2]" data-testid={`expense-row-${e.id}`}>
                    <TableCell className="py-4 text-sm">{e.date}</TableCell>
                    <TableCell><Badge className="bg-[#C28A2E]/12 text-[#7A4A18] rounded-full font-medium px-3">{e.category}</Badge></TableCell>
                    <TableCell className="text-sm">{e.description || "—"}</TableCell>
                    <TableCell className="text-xs text-[#6B5944]">{e.workshop_name || e.client_name || "—"}</TableCell>
                    <TableCell className="text-right font-semibold text-[#C28A2E]">{fmt$(e.amount)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => { setEditing(e); setFormOpen(true); }} data-testid={`edit-expense-${e.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => onDelete(e.id)} data-testid={`delete-expense-${e.id}`}>
                        <Trash2 className="w-4 h-4 text-[#A23E3E]" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#6B5944]">
                    <Receipt className="w-8 h-8 text-[#C28A2E] mx-auto mb-2" strokeWidth={1.5} />
                    No expenses yet. Click "Log expense" to start tracking.
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* REPORTS */}
        <TabsContent value="reports" className="mt-5 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="ccs-card p-6">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#6B8E5A]" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Most Profitable Services</div>
              </div>
              <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">Top profit makers</h3>
              <div className="mt-4 space-y-2.5">
                {data.most_profitable_services?.length > 0 ? data.most_profitable_services.map((s, i) => (
                  <div key={s.service} className="flex items-center justify-between p-3 rounded-lg bg-[#FDFBF5] border border-[#EBE3D2]">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#6B8E5A]/15 text-[#6B8E5A] text-xs font-bold flex items-center justify-center">{i + 1}</div>
                      <div className="font-medium text-sm text-[#2A1810]">{s.service}</div>
                    </div>
                    <div className={`font-semibold ${s.profit >= 0 ? "text-[#6B8E5A]" : "text-[#A23E3E]"}`}>{fmt$(s.profit)}</div>
                  </div>
                )) : <div className="text-sm text-[#6B5944] text-center py-6">No data yet</div>}
              </div>
            </div>

            <div className="ccs-card p-6">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#6B8E5A]" />
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Most Profitable Clients</div>
              </div>
              <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">Your top customers</h3>
              <div className="mt-4 space-y-2.5">
                {data.most_profitable_clients?.length > 0 ? data.most_profitable_clients.map((c, i) => (
                  <div key={c.client} className="flex items-center justify-between p-3 rounded-lg bg-[#FDFBF5] border border-[#EBE3D2]">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#5D3A1F]/15 text-[#5D3A1F] text-xs font-bold flex items-center justify-center">{i + 1}</div>
                      <div className="font-medium text-sm text-[#2A1810]">{c.client}</div>
                    </div>
                    <div className={`font-semibold ${c.profit >= 0 ? "text-[#6B8E5A]" : "text-[#A23E3E]"}`}>{fmt$(c.profit)}</div>
                  </div>
                )) : <div className="text-sm text-[#6B5944] text-center py-6">No data yet</div>}
              </div>
            </div>
          </div>

          <div className="ccs-card p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C28A2E] font-bold">Monthly Financial Summary</div>
            <h3 className="font-display text-lg font-semibold text-[#2A1810] mt-1">Revenue & expense trend</h3>
            <div className="mt-4">
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={data.revenue_vs_expense}>
                  <CartesianGrid stroke="#EBE3D2" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" stroke="#6B5944" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#6B5944" tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #EBE3D2", borderRadius: 12 }} formatter={(v) => fmt$(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="revenue" stroke="#5D3A1F" strokeWidth={2.5} dot={{ r: 4, fill: "#5D3A1F" }} />
                  <Line type="monotone" dataKey="expenses" stroke="#C28A2E" strokeWidth={2.5} dot={{ r: 4, fill: "#C28A2E" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <ExpenseForm
        open={formOpen} onOpenChange={setFormOpen} initial={editing}
        mode={editing ? "edit" : "create"} onSubmit={editing ? onUpdate : onCreate}
        clients={clients} workshops={workshops} ref={ref}
      />
    </div>
  );
};

export default FinancePage;
